import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { isApolloError } from '@apollo/client';

import { useDeletedUserLoginPopup } from '@ocx-app/components/modals';
import { sharedStorage } from '@ocx-app/modules/shared-storage/shared.storage';
import { useGetNavigationActions } from '@ocx-app/hooks/useGetNavigationActions';
import { OtpFillPlugin } from '@ocx-app/lib/otp-fill/OtpFillPlugin';
import { authService } from '@ocx-app/modules/auth/instances/authService';
import { clearCache } from '@ocx-app/modules/cache-layer/utils';
import { AppRoutePath } from '@ocx-app/pages/AppRoutePath';
import { useShowGenericErrorSnackbar } from '@ocx-app/hooks/useShowGenericErrorSnackbar';
import { useSnackbar } from '@ocx-app/lib/snackbar/snackbar';
import { useNavigateAfterSignIn } from '@ocx-app/modules/auth/use-navigate-after-sign-in.hook';

import { useConfiguration } from '@ocx/data-configuration';
import { AuthMethod, OtpChannel } from '@ocx/graphql';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useGetSmsOptedOutWarningPopup } from '@ocx/feat-sms-opted-out-warning';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useInactivePhoneNumberWarningPopup } from '@ocx/feat-inactive-phone-number-warning';
import { formatApolloError, getErrorWithExtensionInfo } from '@ocx/apollo-client';
import { logInDev } from '@ocx/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { RECEIVER_TRIED_TO_USE_CODE_AT } from '@ocx/feat-referrals';

import { RequestMagicLinkParams, RequestOtpParams } from './signup.types';
import { OC_MEMBERSHIP_DELETED_ERROR, SMS_ATTEMPT_TIMEOUT_MILLISECONDS } from './signup.constants';
import { useSignupWithOTP } from './use-signup-with-otp.hook';
import { useRequestOTP } from './use-request-otp.hook';
import { useRequestMagicLink } from './use-request-magic-link.hook';
import { messages } from './signup.messages';
import { useSignupEvents } from './use-signup-events.hook';
import { getIsPhoneNumberBlocked, getIsPhoneNumberInactive } from './signup.utils';

export const useSignupController = () => {
  const { config } = useConfiguration();
  const navigate = useNavigate();
  const navigateAfterSignIn = useNavigateAfterSignIn();
  const snackbar = useSnackbar();
  const intl = useIntl();

  const { triggerSignupRequested, triggerSignupSessionCreated } = useSignupEvents();

  const [phoneNumber, _setPhoneNumber] = useState<string | null>(null);
  const setPhoneNumber = useCallback((value: string | null) => _setPhoneNumber(value), []);
  const [otpSession, setOTPSession] = useState<string | null>(null);

  const { requestMagicLink, isLoading: isRequestMagicLinkLoading } = useRequestMagicLink();
  const { requestOTP, isLoading: isRequestOTPLoading } = useRequestOTP();
  const { signupWithOTP, isLoading: isSubmitOTPLoading } = useSignupWithOTP();
  const showGenericError = useShowGenericErrorSnackbar();
  const { open: openDeletedUserLoginPopup } = useDeletedUserLoginPopup();
  const openBlockedPhoneNumberPopup = useGetSmsOptedOutWarningPopup();
  const { pushRequestLink } = useGetNavigationActions();
  const { open: openInactivePhoneNumberPopup } = useInactivePhoneNumberWarningPopup();

  const [isBlocked, setIsBlocked] = useState(false);
  const block = useCallback(() => {
    setIsBlocked(true);
  }, [setIsBlocked]);

  const isSmsOptedOutWarningEnabled = config.smsOptedOutWarning.enabled;

  useEffect(() => {
    let timeout: number;
    if (isBlocked) {
      timeout = setTimeout(setIsBlocked, SMS_ATTEMPT_TIMEOUT_MILLISECONDS, false) as unknown as number;
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [setIsBlocked, isBlocked]);

  const showIncorrectOTPEnteredMessage = useCallback(() => {
    snackbar.open(intl.formatMessage(messages['signup:incorrect-otp-entered:message']), { variant: 'error' });
  }, [intl, snackbar]);

  const handleSignUpError = useCallback(
    (error: unknown) => {
      if (!(error instanceof Error) || !isApolloError(error)) {
        showGenericError();
        return;
      }
      if (getErrorWithExtensionInfo(error.graphQLErrors)?.extensionCode === OC_MEMBERSHIP_DELETED_ERROR) {
        /* TODO OC_MEMBERSHIP_DELETED_ERROR case should not be an API Error response, there should be a change on the API then
        it will be processed as a normal API response in try{...} block */
        openDeletedUserLoginPopup({ onClosed: pushRequestLink });
        return;
      }
      const message = formatApolloError(error, {
        ignoreGraphqlError: true,
        ignoreNetworkError: true,
      });
      snackbar.open(message, {
        variant: 'error',
      });
    },
    [snackbar, showGenericError, openDeletedUserLoginPopup, pushRequestLink],
  );

  const handleRequestMagicLink = useCallback(
    async ({ phoneNumber, referralCode }: RequestMagicLinkParams): Promise<boolean> => {
      try {
        block();
        setPhoneNumber(phoneNumber);
        const res = await requestMagicLink({ phoneNumber, referralCode });
        if (!res.success) {
          showGenericError();
          triggerSignupRequested({ method: AuthMethod.MagicLink }, false, !!referralCode);
          return false;
        }

        triggerSignupRequested({ method: AuthMethod.MagicLink }, true, !!referralCode);
        sharedStorage.setItem(RECEIVER_TRIED_TO_USE_CODE_AT, referralCode ? new Date() : null).catch(logInDev);
        navigate(AppRoutePath.AuthWaitMessage, { replace: true });
        return true;
      } catch (error) {
        const isPhoneNumberBlocked = getIsPhoneNumberBlocked(error);
        const isPhoneNumberInactive = getIsPhoneNumberInactive(error);

        if (isPhoneNumberBlocked && isSmsOptedOutWarningEnabled) {
          openBlockedPhoneNumberPopup();
        } else if (isPhoneNumberInactive) {
          openInactivePhoneNumberPopup({ onCancel: pushRequestLink });
        } else {
          handleSignUpError(error);
        }

        return false;
      }
    },
    [
      block,
      setPhoneNumber,
      requestMagicLink,
      triggerSignupRequested,
      navigate,
      handleSignUpError,
      showGenericError,
      isSmsOptedOutWarningEnabled,
      openBlockedPhoneNumberPopup,
      openInactivePhoneNumberPopup,
      pushRequestLink,
    ],
  );

  const handleRequestOTP = useCallback(
    async (params: RequestOtpParams): Promise<boolean> => {
      try {
        const { phoneNumber, otpChannel, referralCode } = params;

        block();
        setPhoneNumber(phoneNumber);
        const res = await requestOTP({ phoneNumber, otpChannel, referralCode });
        if (!res) {
          showGenericError();
          triggerSignupRequested({ method: AuthMethod.Otp, otpChannel }, false, !!referralCode);
          return false;
        }

        setOTPSession(res.session);
        triggerSignupRequested({ method: AuthMethod.Otp, otpChannel }, true, !!referralCode);
        sharedStorage.setItem(RECEIVER_TRIED_TO_USE_CODE_AT, referralCode ? new Date() : null).catch(logInDev);

        if (otpChannel === OtpChannel.Sms) {
          navigate(AppRoutePath.AuthOtpSms, { replace: true });
        }
        if (otpChannel === OtpChannel.PhoneCall) {
          navigate(AppRoutePath.AuthOtpPhoneCall, { replace: true });
        }

        return true;
      } catch (error) {
        const isPhoneNumberBlocked = getIsPhoneNumberBlocked(error);
        const isPhoneNumberInactive = getIsPhoneNumberInactive(error);

        if (isPhoneNumberBlocked && isSmsOptedOutWarningEnabled) {
          openBlockedPhoneNumberPopup();
        } else if (isPhoneNumberInactive) {
          openInactivePhoneNumberPopup({ onCancel: pushRequestLink });
        } else {
          handleSignUpError(error);
        }

        return false;
      }
    },
    [
      setPhoneNumber,
      block,
      requestOTP,
      triggerSignupRequested,
      navigate,
      handleSignUpError,
      showGenericError,
      isSmsOptedOutWarningEnabled,
      openBlockedPhoneNumberPopup,
      openInactivePhoneNumberPopup,
      pushRequestLink,
    ],
  );

  const handleSubmitOTP = useCallback(
    async (params: { otpValue: string; otpChannel: OtpChannel }) => {
      try {
        if (!otpSession) {
          return;
        }
        const { otpValue, otpChannel } = params;
        const res = await signupWithOTP({
          otp: otpValue,
          session: otpSession,
        });

        if (!res.tokens) {
          showIncorrectOTPEnteredMessage();
          return;
        }

        authService.setTokens(res.tokens.accessToken, res.tokens.refreshToken);
        await clearCache().catch(logInDev);
        triggerSignupSessionCreated({ method: AuthMethod.Otp, otpChannel });
        navigateAfterSignIn();
      } catch (e) {
        handleSignUpError(e);
      }
    },
    [
      navigateAfterSignIn,
      otpSession,
      handleSignUpError,
      showIncorrectOTPEnteredMessage,
      signupWithOTP,
      triggerSignupSessionCreated,
    ],
  );

  useEffect(() => {
    OtpFillPlugin.addOtpListener((otp) => {
      if (!otp) {
        return;
      }
      handleSubmitOTP({ otpValue: otp.value, otpChannel: OtpChannel.Sms });
    });

    return () => {
      OtpFillPlugin.removeOtpListeners();
    };
  }, [handleSubmitOTP]);

  return {
    setPhoneNumber,
    phoneNumber,
    handleRequestMagicLink,
    handleRequestOTP,
    handleSubmitOTP,
    isBlockedByTimeout: isBlocked,
    isRequestMagicLinkLoading,
    isRequestOTPLoading,
    isSubmitOTPLoading,
  };
};
